/**
 * Rail — 64 px vertical icon nav with hover/focus peek-expand.
 *
 *   <Rail brand="L" version="v8.0.0">
 *     <Rail.Item icon="car"   label="Vozidla" active />
 *     <Rail.Item icon="users" label="Klienti" />
 *   </Rail>
 *
 * Active item shows a 14 × 2 px accent underline. Items are buttons so the
 * keyboard `Tab` traversal is well-defined; `label` becomes the tooltip and
 * accessible name.
 *
 * Layout: the outer wrapper stays 64 px (the Shell grid track is fixed at
 * 64 px). The inner <nav> is absolutely positioned and widens to 220 px on
 * pointer hover or keyboard focus-within, overlaying the center column —
 * so the rest of the workspace never reflows and no per-user "expanded"
 * preference needs to be persisted.
 *
 * Source: .agents/design/project/artboard-radical.jsx:38-67
 */

import { Link } from '@tanstack/react-router';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { BrandLogo } from './BrandLogo';
import { Icon, type IconName } from './Icon';

interface RailProps {
  /** One- or two-character brand mark in the head. Wrapped in the default
   * 36×36 ink box unless `unstyledBrand` is set. */
  brand: ReactNode;
  /** Skip the default brand-mark wrapper — useful when `brand` is itself an
   * interactive control (e.g. `<UserMenuBrand />`) that owns its own styling. */
  unstyledBrand?: boolean;
  /** Optional version label rotated -90° at the bottom. */
  version?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Rail({ brand, unstyledBrand, version, children, className = '' }: RailProps) {
  return (
    // Phase 14.1: hidden on phone (AppTopBar + NavDrawer replace it below
    // `sm`); icon-only on tablet — the peek-expand is gated to `lg:` because
    // hover has no touch equivalent and tap-focus would otherwise pop the
    // 220px overlay on every tablet navigation.
    <div className={'relative h-full w-16 max-sm:hidden ' + className}>
      <nav
        aria-label="Hlavní navigace"
        className={
          'group/rail absolute inset-y-0 left-0 z-30 flex w-16 flex-col items-stretch gap-1 ' +
          'overflow-hidden border-r border-line bg-paper py-4 ' +
          'transition-[width,box-shadow] duration-200 ease-out ' +
          'lg:hover:w-[220px] lg:focus-within:w-[220px] ' +
          'lg:hover:shadow-[4px_0_16px_rgba(0,0,0,0.06)] lg:focus-within:shadow-[4px_0_16px_rgba(0,0,0,0.06)]'
        }
      >
        {/* pl-[14px] keeps the 36×36 brand visually centered in the 64 px
            collapsed column AND aligned with the icon column when expanded,
            so the brand doesn't shift sideways on hover. */}
        <div className="mb-4 flex pl-[14px]">
          {unstyledBrand ? (
            brand
          ) : (
            <div className="grid size-9 place-items-center bg-ink font-mono text-[14px] font-bold tracking-[-0.05em] text-paper">
              {brand}
            </div>
          )}
        </div>
        {children}
        <div className="flex-1" />
        {version && (
          <div className="my-6 flex pl-[14px]">
            <span className="-rotate-90 whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.1em] text-ink-3">
              {version}
            </span>
          </div>
        )}
        {/* Centered in the 64 px collapsed column (pl = (64 − 9) / 2). */}
        <div className="mt-1 flex pl-[27px]">
          <BrandLogo height={27} />
        </div>
      </nav>
    </div>
  );
}

interface RailItemBaseProps {
  icon: IconName;
  /** Accessible name + tooltip + peek-expand label. */
  label: string;
  active?: boolean;
  className?: string;
}

interface RailItemProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>, RailItemBaseProps {}

interface RailLinkItemProps extends RailItemBaseProps {
  to: string;
}

function railItemClass(active: boolean, className = '') {
  return (
    'group relative flex h-10 w-full shrink-0 items-center gap-3 bg-transparent pl-[23px] pr-3 transition-colors ' +
    (active ? 'text-ink' : 'text-ink-2 hover:text-ink') +
    ' ' +
    className
  );
}

function RailItemContent({ icon, label, active }: { icon: IconName; label: string; active: boolean }) {
  return (
    <>
      <Icon name={icon} size={18} stroke={1.75} />
      <span
        aria-hidden
        className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.1em] opacity-0 transition-opacity duration-150 lg:group-hover/rail:opacity-100 lg:group-focus-within/rail:opacity-100"
      >
        {label}
      </span>
      {active && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-1 left-[23px] h-0.5 w-[18px] bg-accent"
          style={{ viewTransitionName: 'rail-active-indicator' }}
        />
      )}
    </>
  );
}

function RailItem({ icon, label, active = false, className = '', ...rest }: RailItemProps) {
  return (
    <button
      {...rest}
      type="button"
      title={label}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={railItemClass(active, className)}
    >
      <RailItemContent icon={icon} label={label} active={active} />
    </button>
  );
}

function RailLinkItem({ icon, label, active = false, className = '', to }: RailLinkItemProps) {
  return (
    <Link
      to={to}
      title={label}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={railItemClass(active, className)}
    >
      <RailItemContent icon={icon} label={label} active={active} />
    </Link>
  );
}

Rail.Item = RailItem;
Rail.LinkItem = RailLinkItem;
